import React, { useState, useEffect } from "react";
import { Stripe, loadStripe } from '@stripe/stripe-js';
import { Card, CardMedia, CardContent, Typography, Button, Box, Grid, Paper } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
    EmbeddedCheckoutProvider,
    EmbeddedCheckout
  } from '@stripe/react-stripe-js';
import { MapComponent } from "./map";
import { callCreateCheckout } from "src/apiCalls";
import { MapComponentObscure } from "./obscureMap";
import { useUserContext } from "src/components/ProtectedRoute/userProvider";
import { SignInModal } from "src/components/Auth/SignInModal";
import { useImageCount } from "../Hooks/useImageCount";
import logger from 'src/logger';
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_live_51O8NgjBnhTe1sNXIWc0hlJZslXopiG4BAt3fSRqnJXoN7lJlkrEzpiz15RTJfMjHNx6zUJL7nfAxnOkmh2hyjiUS00dLWsxd3W"; // Your key here

// Initialize Stripe with Connect account
const getStripePromise = (stripeAccountId) => 
  loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: stripeAccountId });

const LocationDisplay = ({ imageData, stripeAccountId }) => {
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [locationRevealed, setLocationRevealed] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const { user } = useUserContext();
  const { imageCount } = useImageCount(user);
  const stripePromise = getStripePromise(stripeAccountId);

  // Check if user is registered (has a UID and email)
  const isUserRegistered = user && user.uid && user.email;
  
  // Check if user can reveal location for free
  const canRevealForFree = isUserRegistered && imageData.freeForRegisteredUsers && 
    (!imageData.minimumPhotoUploads || imageCount >= imageData.minimumPhotoUploads);
  
  // Check if location is free for everyone
  const isFreeForEveryone = imageData.freeForEveryone;

  // Remove the problematic loading state check - it was causing issues for logged-out users
  // The user context will handle its own loading state if needed

  // Debug logging
  console.log('LocationDisplay Debug:', {
    user: !!user,
    userDetails: user ? {
      uid: user.uid,
      email: user.email
    } : null,
    isUserRegistered,
    imageCount,
    imageData: {
      chargeForLocation: imageData.chargeForLocation,
      freeForRegisteredUsers: imageData.freeForRegisteredUsers,
      freeForEveryone: imageData.freeForEveryone,
      minimumPhotoUploads: imageData.minimumPhotoUploads,
      publicise: imageData.publicise
    },
    rawImageData: imageData, // Show the complete image data
    canRevealForFree,
    isFreeForEveryone,
    locationRevealed
  });

  // Additional detailed logging
  console.log('Detailed Access Control Analysis:', {
    'user exists': !!user,
    'user.uid': user?.uid,
    'user.email': user?.email,
    'imageCount': imageCount,
    'imageData.chargeForLocation': imageData.chargeForLocation,
    'imageData.freeForRegisteredUsers': imageData.freeForRegisteredUsers,
    'imageData.freeForEveryone': imageData.freeForEveryone,
    'imageData.minimumPhotoUploads': imageData.minimumPhotoUploads,
    'imageData.publicise': imageData.publicise,
    'isUserRegistered calculation': user && user.uid && user.email,
    'canRevealForFree calculation': (user && user.uid && user.email) && imageData.freeForRegisteredUsers && (!imageData.minimumPhotoUploads || imageCount >= imageData.minimumPhotoUploads),
    'final canRevealForFree': canRevealForFree,
    'Note': 'User considered registered if they have UID and email. Must also meet minimum photo uploads requirement if set.'
  });

  useEffect(() => {
    console.log('useEffect running with:', {
      isFreeForEveryone,
      canRevealForFree,
      currentLocationRevealed: locationRevealed
    });
    
    // Only automatically reveal if:
    // 1. Location is free for everyone, OR  
    // 2. User is logged in AND can reveal for free (registered user benefit)
    // 
    // DO NOT automatically reveal if:
    // - Location charges for access (chargeForLocation: true)
    // - Location is only free for registered users but user is not logged in
    if (isFreeForEveryone || canRevealForFree) {
      console.log('Setting locationRevealed to TRUE');
      setLocationRevealed(true);
    } else {
      console.log('Setting locationRevealed to FALSE');
      setLocationRevealed(false);
    }
  }, [isFreeForEveryone, canRevealForFree]);

  const handleComplete = async () => {
    window.location.href = `/return?session_id=${clientSecret}&id=${imageData.id}`;
  };

  const handleCreateCheckout = async () => {
    try {
      const checkoutData = await callCreateCheckout(imageData.id, stripeAccountId);
      setClientSecret(checkoutData.clientSecret);
      setShowPaymentForm(true);
    } catch (error) {
      logger.error('Error creating checkout:', error);
    }
  };

  // Location preview (blurred map)
  const LocationPreview = () => (
    <Card sx={{ position: 'relative', overflow: 'hidden' }}>
  <MapComponentObscure 
    longitude={imageData.longitude} 
    latitude={imageData.latitude} 

  />
  <CardContent
    sx={{
      position: 'absolute',
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      width: '100%',
      textAlign: 'center',
      color: 'white',
      py: 0,
    }}
  >
    <Typography variant="body2">
      {imageData.chargeForLocation 
        ? canRevealForFree 
          ? imageData.minimumPhotoUploads 
            ? `Click to view location (free for registered users with ${imageData.minimumPhotoUploads}+ uploads)`
            : 'Click to view location (free for registered users)'
          : 'Make a payment to reveal location'
        : imageData.freeForRegisteredUsers
          ? user 
            ? canRevealForFree
              ? imageData.minimumPhotoUploads 
                ? `Click to view location (free for registered users with ${imageData.minimumPhotoUploads}+ uploads)`
                : 'Click to view location (free for registered users)'
              : `Log in to view location (requires ${imageData.minimumPhotoUploads} photo uploads)`
            : imageData.minimumPhotoUploads 
              ? `Log in to view location (free for registered users with ${imageData.minimumPhotoUploads} photo uploads)`
              : 'Log in to view location (free for registered users)'
          : imageData.freeForEveryone
            ? 'Click to view location (free for everyone)'
            : 'Click to view location'}
    </Typography>
  </CardContent>
</Card>

  );

  // Full location display
  const LocationDetails = () => {
    console.log('LocationDetails component called');
    
    // Check access control before showing location details
    if (!imageData.publicise) {
      console.log('LocationDetails: publicise is false, returning null');
      return null;
    }
    
    // Additional access control checks
    if (imageData.chargeForLocation && !canRevealForFree) {
      console.log('LocationDetails: chargeForLocation true but canRevealForFree false, returning null');
      return null;
    }
    
    if (imageData.freeForRegisteredUsers && !user) {
      console.log('LocationDetails: freeForRegisteredUsers true but no user, returning null');
      return null;
    }
    
    console.log('LocationDetails: All checks passed, rendering location details');
    return (
      <>
        <Box sx={{ height: 400, width: '100%', borderRadius: 1, overflow: 'hidden' }}>
          <MapComponent 
            longitude={imageData.longitude} 
            latitude={imageData.latitude} 
          />
        </Box>
        <Paper elevation={3} sx={{ p: 1, mb: 1 }}>
          <Typography variant="h5" gutterBottom>
            Location Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Latitude
              </Typography>
              <Typography variant="h6">
                {imageData.latitude.toFixed(6)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" color="text.secondary">
                Longitude
              </Typography>
              <Typography variant="h6">
                {imageData.longitude.toFixed(6)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </>
    );
  };
  
  return (
    <Box sx={{ mt: 2 }}>
      {console.log('Main render - locationRevealed:', locationRevealed, 'canRevealForFree:', canRevealForFree)}
      {!locationRevealed ? (
        <>
          {console.log('Rendering LocationPreview branch')}
          <LocationPreview />
          {imageData.chargeForLocation && !canRevealForFree && imageData.price && (
            <Box sx={{
              mt: 2,
              p: 2,
              bgcolor: 'rgba(0, 0, 0, 0.04)',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon color="primary" />
                <Typography variant="body2">
                Catch location available for £{imageData.price.toFixed(2)}
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={handleCreateCheckout}
                disabled={showPaymentForm}
              >
                Purchase Location
              </Button>
            </Box>
          )}
          {imageData.chargeForLocation && !canRevealForFree && !imageData.price && (
            <Box sx={{
              mt: 2,
              p: 2,
              bgcolor: 'rgba(255, 152, 0, 0.1)',
              borderRadius: 1,
              border: '1px solid rgba(255, 152, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}>
              <LocationOnIcon color="warning" />
              <Typography variant="body2" color="warning.main">
                Location pricing not configured. Please contact support.
              </Typography>
            </Box>
          )}
          {!imageData.chargeForLocation && !imageData.freeForRegisteredUsers && !imageData.freeForEveryone && (
            <Button
              fullWidth
              variant="contained"
              onClick={() => setLocationRevealed(true)}
              sx={{ mt: 2 }}
            >
              View Location
            </Button>
          )}
          {imageData.freeForRegisteredUsers && !user && (
            <Box sx={{
              mt: 2,
              p: 2,
              bgcolor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: 1,
              border: '1px solid rgba(76, 175, 80, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon color="success" />
                <Typography variant="body2" color="success.main" textAlign="center">
                  {imageData.minimumPhotoUploads 
                    ? `This location is free for registered users with ${imageData.minimumPhotoUploads} photo uploads. Please log in to view the location.`
                    : 'This location is free for registered users. Please log in to view the location.'
                  }
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => setShowSignInModal(true)}
              >
                Log In
              </Button>
            </Box>
          )}
          {imageData.freeForRegisteredUsers && user && !canRevealForFree && imageData.minimumPhotoUploads && (
            <Box sx={{
              mt: 2,
              p: 2,
              bgcolor: 'rgba(255, 152, 0, 0.1)',
              borderRadius: 1,
              border: '1px solid rgba(255, 152, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon color="warning" />
                <Typography variant="body2" color="warning.main" textAlign="center">
                  This location requires {imageData.minimumPhotoUploads} photo uploads to access for free. You have uploaded {imageCount} photos.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="warning"
                size="small"
                onClick={() => window.location.href = '/images/addsingle'}
              >
                Upload More Photos
              </Button>
            </Box>
          )}
          {canRevealForFree && (
            <Box sx={{
              mt: 2,
              p: 2,
              bgcolor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: 1,
              border: '1px solid rgba(76, 175, 80, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon color="success" />
                <Typography variant="body2" color="success.main">
                  {imageData.minimumPhotoUploads 
                    ? `Location available for free (registered user with ${imageData.minimumPhotoUploads}+ uploads)`
                    : 'Location available for free (registered user benefit)'
                  }
                </Typography>
              </Box>
            </Box>
          )}
          {isFreeForEveryone && (
            <Box sx={{
              mt: 2,
              p: 2,
              bgcolor: 'rgba(33, 150, 243, 0.1)',
              borderRadius: 1,
              border: '1px solid rgba(33, 150, 243, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon color="info" />
                <Typography variant="body2" color="info.main">
                  Location available for free (public benefit)
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="info"
                onClick={() => setLocationRevealed(true)}
              >
                View Location
              </Button>
              </Box>
          )}
          {showPaymentForm && clientSecret && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom sx={{ textAlign: 'center' }}>
                Complete the payment below to unlock the location.
              </Typography>
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  clientSecret,
                  onComplete: handleComplete
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </Box>
          )}
        </>
      ) : (
        // Show LocationDetails when locationRevealed is true
        <>
          {console.log('Rendering LocationDetails branch')}
          <LocationDetails />
        </>
      )}
      
      {/* Sign In Modal */}
      <SignInModal
        open={showSignInModal}
        onClose={() => setShowSignInModal(false)}
      />
    </Box>
  );
};

export default LocationDisplay;